import { injectable, inject } from 'inversify';
import { DDD_SYMBOLS } from '../../infrastructure/di/DDD_SYMBOLS';
import { CreateAggregateCommand } from '../../bounded-contexts/tactical-design/application/commands/CreateAggregateCommand';
import { CreateAggregateHandler } from '../../bounded-contexts/tactical-design/application/handlers/CreateAggregateHandler';
import { GetAggregateHandler } from '../../bounded-contexts/tactical-design/application/handlers/GetAggregateHandler';
import { GetAggregateQuery, ListAggregatesQuery } from '../../bounded-contexts/tactical-design/application/queries/GetAggregateQuery';
import { AggregateViewModel, AggregateSummaryViewModel, AggregateDesignViewModel } from '../../bounded-contexts/tactical-design/interface/view-models/AggregateViewModel';
import { CreateAggregateRequest, UpdateAggregateRequest, GetAggregatesQuery } from '../../bounded-contexts/tactical-design/interface/dto/AggregateRequestDTOs';
import { AggregateQueryService } from '../../bounded-contexts/tactical-design/application/services/AggregateQueryService';

/**
 * Aggregate Application Service
 * 
 * Orchestrates aggregate operations and provides the boundary between
 * the API layer and the domain/application layers.
 * 
 * This is the ONLY allowed binding point for aggregate API controllers.
 */
@injectable()
export class AggregateApplicationService {
    constructor(
        @inject(DDD_SYMBOLS.CreateAggregateHandler) 
        private readonly createAggregateHandler: CreateAggregateHandler,
        
        @inject(DDD_SYMBOLS.GetAggregateHandler)
        private readonly getAggregateHandler: GetAggregateHandler,
        
        @inject(DDD_SYMBOLS.AggregateQueryService)
        private readonly aggregateQueryService: AggregateQueryService
    ) {}

    /**
     * Create a new aggregate
     */
    async createAggregate(request: CreateAggregateRequest): Promise<AggregateViewModel> {
        const command = new CreateAggregateCommand(
            {
                name: request.name,
                description: request.description,
                responsibility: request.purpose || 'Aggregate responsibility',
                boundedContextId: request.boundedContextId,
                rootEntity: {
                    name: request.name,
                    description: request.description,
                    properties: []
                }
            },
            'api-session',
            'api-user'
        );

        const result = await this.createAggregateHandler.handle(command);
        
        return this.mapToAggregateViewModel(result);
    }

    /**
     * Get all aggregates with optional filtering
     */
    async getAggregates(query: GetAggregatesQuery): Promise<AggregateSummaryViewModel[]> {
        const listQuery = new ListAggregatesQuery(
            query.boundedContextId,
            query.domainId,
            false,
            'api-session',
            'api-user'
        );

        // Use real AggregateQueryService with proper filtering
        const aggregateResults = await this.aggregateQueryService.listAggregates({
            boundedContextId: query.boundedContextId,
            domainId: query.domainId,
            nameFilter: query.search
        });
        
        // Apply pagination (the query service doesn't handle this)
        const paginatedResults = aggregateResults.slice(query.offset, query.offset + query.limit);
        
        return paginatedResults.map(aggregate => ({
            id: aggregate.aggregateId,
            name: aggregate.name,
            boundedContextName: 'Unknown Context', // TODO: Get from bounded context lookup
            domainName: 'Unknown Domain', // TODO: Get from domain lookup
            status: this.mapAggregateStatus('implemented'), // TODO: Map from aggregate.status when available
            entityCount: aggregate.childEntities?.length + 1 || 1, // +1 for root entity
            valueObjectCount: aggregate.valueObjects?.length || 0,
            complexity: this.calculateComplexityFromDto(aggregate),
            lastUpdated: (aggregate.updatedAt || aggregate.createdAt)?.toISOString() || new Date().toISOString()
        }));
    }

    /**
     * Get a specific aggregate by ID
     */
    async getAggregate(aggregateId: string): Promise<AggregateViewModel> {
        const query = new GetAggregateQuery(aggregateId, 'api-session', 'api-user');
        const result = await this.getAggregateHandler.handle(query);
        
        return this.mapToAggregateViewModel(result);
    }

    /**
     * Get aggregate design view with patterns and decisions
     */
    async getAggregateDesign(aggregateId: string): Promise<AggregateDesignViewModel> {
        const aggregate = await this.getAggregate(aggregateId);
        
        // This would normally come from a design tracking system
        const designDecisions = [
            {
                decision: "Use Command pattern for aggregate operations",
                rationale: "Ensures consistent business rule enforcement and audit trail",
                alternatives: ["Direct method calls", "Event-driven updates"],
                madeAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                madeBy: "Domain Expert"
            }
        ];

        const patterns = [
            {
                pattern: "Aggregate Root",
                application: "Single entry point for all aggregate modifications",
                benefits: ["Consistency", "Encapsulation", "Transaction boundaries"],
                tradeoffs: ["Potential performance bottlenecks", "Complexity in cross-aggregate operations"]
            }
        ];

        const relationships = [
            {
                type: "references" as const,
                targetAggregate: "related-aggregate-id",
                description: "References related aggregate by ID only"
            }
        ];

        return {
            aggregate,
            designDecisions,
            patterns,
            relationships
        };
    }

    /**
     * Map internal aggregate result to View Model
     */
    private mapToAggregateViewModel(result: any): AggregateViewModel {
        return {
            id: result.id || result.aggregateId,
            name: result.name,
            description: result.description,
            purpose: result.purpose || result.description,
            boundedContextId: result.boundedContextId,
            boundedContextName: result.boundedContextName || 'Unknown Context',
            domainId: result.domainId,
            domainName: result.domainName || 'Unknown Domain',
            status: this.mapAggregateStatus(result.status),
            createdAt: result.createdAt || new Date().toISOString(),
            updatedAt: result.updatedAt || result.createdAt || new Date().toISOString(),
            structure: {
                rootEntity: {
                    name: result.structure?.rootEntity?.name || result.name,
                    description: result.structure?.rootEntity?.description || 'Aggregate root entity'
                },
                entities: (result.structure?.entities || []).map((entity: any) => ({
                    name: entity.name,
                    description: entity.description,
                    keyAttributes: entity.keyAttributes || []
                })),
                valueObjects: (result.structure?.valueObjects || []).map((vo: any) => ({
                    name: vo.name,
                    description: vo.description,
                    properties: vo.properties || []
                })),
                domainServices: (result.structure?.domainServices || []).map((service: any) => ({
                    name: service.name,
                    description: service.description,
                    operations: service.operations || []
                }))
            },
            behavior: {
                commands: (result.behavior?.commands || []).map((cmd: any) => ({
                    name: cmd.name,
                    description: cmd.description,
                    parameters: cmd.parameters || []
                })),
                events: (result.behavior?.events || []).map((event: any) => ({
                    name: event.name,
                    description: event.description,
                    properties: event.properties || []
                })),
                queries: (result.behavior?.queries || []).map((query: any) => ({
                    name: query.name,
                    description: query.description,
                    returnType: query.returnType || 'Object'
                }))
            },
            invariants: (result.invariants || []).map((inv: any) => ({
                name: inv.name,
                description: inv.description,
                rule: inv.rule
            })),
            complexity: this.calculateComplexity(result)
        };
    }

    /**
     * Map aggregate status from internal to external representation
     */
    private mapAggregateStatus(internalStatus: string | undefined): 'designed' | 'implementing' | 'implemented' | 'refactoring' {
        switch (internalStatus) {
            case 'DESIGNED': return 'designed';
            case 'IMPLEMENTING': return 'implementing';
            case 'IMPLEMENTED': return 'implemented';
            case 'REFACTORING': return 'refactoring';
            default: return 'designed';
        }
    }

    /**
     * Calculate aggregate complexity
     */
    private calculateComplexity(aggregate: any): 'simple' | 'moderate' | 'complex' | 'very_complex' {
        const entities = aggregate.structure?.entities?.length || 0;
        const valueObjects = aggregate.structure?.valueObjects?.length || 0;
        const commands = aggregate.behavior?.commands?.length || 0;
        const events = aggregate.behavior?.events?.length || 0;
        const invariants = aggregate.invariants?.length || 0;
        
        const complexityScore = entities * 3 + valueObjects * 2 + commands * 2 + events + invariants * 2;
        
        if (complexityScore < 8) return 'simple';
        if (complexityScore < 20) return 'moderate';
        if (complexityScore < 40) return 'complex';
        return 'very_complex';
    }

    /**
     * Calculate aggregate complexity from DTO
     */
    private calculateComplexityFromDto(aggregate: any): 'simple' | 'moderate' | 'complex' | 'very_complex' {
        const entities = (aggregate.childEntities?.length || 0) + 1; // +1 for root entity
        const valueObjects = aggregate.valueObjects?.length || 0;
        const commands = aggregate.domainEventIds?.length || 0; // Approximate
        const events = aggregate.domainEventIds?.length || 0;
        const invariants = aggregate.businessInvariantIds?.length || 0;
        
        const complexityScore = entities * 3 + valueObjects * 2 + commands * 2 + events + invariants * 2;
        
        if (complexityScore < 8) return 'simple';
        if (complexityScore < 20) return 'moderate';
        if (complexityScore < 40) return 'complex';
        return 'very_complex';
    }

}